import React, { useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Modal from '../modal/Modal';
import { getFileIcon, canPreviewFile, getPreviewUrl } from '../../../utils/filePreview';
import { dateOnlyFormatted } from '../../../utils/systemSettings';
import './FileUpload.scss';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to get file type from URL extension
const getFileTypeFromUrl = (url) => {
  if (!url) return 'application/octet-stream';

  const extension = url.split('.').pop()?.toLowerCase();
  const mimeTypes = {
    // Images
    'jpg': 'image/jpeg', 'jpeg': 'image/jpeg', 'png': 'image/png', 'gif': 'image/gif',
    'bmp': 'image/bmp', 'webp': 'image/webp', 'svg': 'image/svg+xml',
    // Documents
    'pdf': 'application/pdf', 'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'txt': 'text/plain', 'csv': 'text/csv',
    // Archives
    'zip': 'application/zip', 'rar': 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    // Audio
    'mp3': 'audio/mpeg', 'wav': 'audio/wav', 'ogg': 'audio/ogg',
    // Video
    'mp4': 'video/mp4', 'mov': 'video/quicktime', 'avi': 'video/x-msvideo'
  };

  return mimeTypes[extension] || 'application/octet-stream';
};

/**
 * FileUpload component with drag & drop functionality.
 * By default, accepts images, documents, archives, audio, and video files.
 * Pass acceptedTypes prop to override default file type restrictions.
 * Works with attachment API format: { id, title, notes, url, dateCreated, creator }
 */
const FileUpload = (props) => {
  const {
    label,
    files = [],
    onUpload,
    onDelete,
    onEdit,
    onPreview,
    onDownload,
    loading = false,
    uploading = false,
    maxFiles = 10,
    maxSize = 20971520, // 20MB
    acceptedTypes = null, // If null, use default accepted types
    wrapperClass,
    disabled = false
  } = props;

  // Default accepted file types
  const defaultAcceptedTypes = {
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff', '.tif', '.svg'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'text/plain': ['.txt'],
    'text/csv': ['.csv'],
    'application/rtf': ['.rtf'],
    'application/vnd.oasis.opendocument.text': ['.odt'],
    'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
    'application/vnd.oasis.opendocument.presentation': ['.odp'],
    'application/zip': ['.zip'],
    'application/x-rar-compressed': ['.rar'],
    'application/x-7z-compressed': ['.7z'],
    'application/x-tar': ['.tar'],
    'application/gzip': ['.gz'],
    'application/x-compressed-tar': ['.tgz'],
    'audio/*': ['.mp3', '.wav', '.ogg', '.m4a', '.flac'],
    'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.wmv']
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteOptions, setShowDeleteOptions] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [fileToEdit, setFileToEdit] = useState(null);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [editTitle, setEditTitle] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Get settings from localStorage for date formatting
  const settings = JSON.parse(localStorage.getItem('settings')) || {};
  const [setting] = useState(settings);

  const dropdownRef = useRef(null);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (disabled) return;

    if (rejectedFiles.length > 0) {
      // Handle rejected files (show error message)
      console.error('Some files were rejected:', rejectedFiles);
      return;
    }

    const filesToProcess = acceptedFiles.slice(0, maxFiles - files.length);

    if (filesToProcess.length === 0) {
      console.error('No files to upload or maximum file limit reached');
      return;
    }

    // Prepare files with default metadata
    const filesWithMetadata = filesToProcess.map((file, index) => ({
      id: `temp-${Date.now()}-${index}`,
      file,
      title: file.name,
      notes: '',
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setFilesToUpload(filesWithMetadata);
    setShowUploadModal(true);
  }, [maxFiles, files.length, disabled]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes || defaultAcceptedTypes,
    maxSize,
    disabled: disabled || loading,
    multiple: maxFiles > 1
  });

  const handlePreview = (file) => {
    if (onPreview) {
      onPreview(file);
    } else {
      setSelectedFile(file);
      setShowPreview(true);
    }
    setActiveDropdown(null);
  };

  const handleDownload = (file) => {
    if (onDownload) {
      onDownload(file);
    } else {
      // Default download functionality
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      link.click();
    }
    setActiveDropdown(null);
  };

  const handleDelete = (file) => {
    setFileToDelete(file);
    setShowDeleteOptions(true);
    setActiveDropdown(null);
  };

  const handleDeleteWithOption = async (hardDelete) => {
    if (!fileToDelete) return;

    if (onDelete) {
      await onDelete(fileToDelete, hardDelete);
    }

    setShowDeleteOptions(false);
    setFileToDelete(null);
  };

  const handleEdit = (file) => {
    setFileToEdit(file);
    setEditTitle(file.title || '');
    setEditNotes(file.notes || '');
    setShowEditModal(true);
    setActiveDropdown(null);
  };

  const handleSaveEdit = async () => {
    if (!fileToEdit) return;

    if (onEdit) {
      await onEdit(fileToEdit.id, {
        title: editTitle,
        notes: editNotes
      });
    }

    setShowEditModal(false);
    setFileToEdit(null);
    setEditTitle('');
    setEditNotes('');
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setFileToEdit(null);
    setEditTitle('');
    setEditNotes('');
  };

  const handleFileMetadataChange = (fileId, field, value) => {
    setFilesToUpload(prev =>
      prev.map(file =>
        file.id === fileId
          ? { ...file, [field]: value }
          : file
      )
    );
  };

  const handleConfirmUpload = async () => {
    if (onUpload && filesToUpload.length > 0) {
      // Create array of files with their metadata for upload
      const filesToSubmit = filesToUpload.map(fileData => {
        const fileWithMetadata = fileData.file;
        fileWithMetadata.uploadTitle = fileData.title;
        fileWithMetadata.uploadNotes = fileData.notes;
        return fileWithMetadata;
      });

      await onUpload(filesToSubmit);
    }

    // Clean up preview URLs
    filesToUpload.forEach(fileData => {
      if (fileData.previewUrl) {
        URL.revokeObjectURL(fileData.previewUrl);
      }
    });

    // Close modal and reset state
    setShowUploadModal(false);
    setFilesToUpload([]);
  };

  const handleCancelUpload = () => {
    // Clean up object URLs for image previews
    filesToUpload.forEach(fileData => {
      if (fileData.file.type.startsWith('image/') && fileData.previewUrl) {
        URL.revokeObjectURL(fileData.previewUrl);
      }
    });
    setShowUploadModal(false);
    setFilesToUpload([]);
  };

  const toggleDropdown = (fileId, event) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === fileId ? null : fileId);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`file-upload ${wrapperClass || ''}`}>
      {label && <label className="file-upload__label">{label}</label>}

      {loading ? (
        <Skeleton height={120} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
      ) : (
        <>
          {/* Upload Area */}
          <div
            {...getRootProps()}
            className={`file-upload__drop-zone ${isDragActive ? 'file-upload__drop-zone--active' : ''
              } ${disabled ? 'file-upload__drop-zone--disabled' : ''}`}
          >
            <input {...getInputProps()} />
            <div className="file-upload__drop-content">
              <div className="file-upload__drop-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 2V18M16 2L10 8M16 2L22 8" stroke="#342C82" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6 18V26C6 27.1046 6.89543 28 8 28H24C25.1046 28 26 27.1046 26 26V18" stroke="#342C82" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="file-upload__drop-text">
                {isDragActive ? (
                  <p>Drop the files here...</p>
                ) : (
                  <>
                    <p>Drag & drop files here, or <span className="file-upload__browse-text">browse</span></p>
                    <p className="file-upload__help-text">
                      (Max {formatFileSize(maxSize)})
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="file-upload__list">
              <div className="file-upload__list-header">
                <span className="file-upload__list-title">
                  Uploaded Files ({files.length})
                </span>
              </div>

              <div className="file-upload__items">
                {files.map((file) => {
                  const fileType = getFileTypeFromUrl(file.url);

                  return (
                    <div key={file.id} className="file-upload__item">
                      <div className="file-upload__item-thumbnail">
                        <img src={fileType.startsWith('image/') ? file.url : getFileIcon(fileType, file.title)} alt={file.title} />
                      </div>

                      <div className="file-upload__item-info">
                        <div className="file-upload__item-name" title={file.title}>
                          {file.title}
                        </div>
                        {file.notes && (
                          <div className="file-upload__item-notes" title={file.notes}>
                            {file.notes}
                          </div>
                        )}
                      </div>

                      <div className="file-upload__item-meta">
                        <div className="file-upload__item-creator">
                          {file.creator?.name || '-'}
                        </div>
                        <div className="file-upload__item-date">
                          {dateOnlyFormatted(setting, file.dateCreated)}
                        </div>
                      </div>

                      <div className="file-upload__item-actions">
                        <button
                          className="file-upload__action-trigger"
                          onClick={(e) => toggleDropdown(file.id, e)}
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.25 10C6.25 10.6904 5.69036 11.25 5 11.25C4.30964 11.25 3.75 10.6904 3.75 10C3.75 9.30964 4.30964 8.75 5 8.75C5.69036 8.75 6.25 9.30964 6.25 10ZM11.25 10C11.25 10.6904 10.6904 11.25 10 11.25C9.30964 11.25 8.75 10.6904 8.75 10C8.75 9.30964 9.30964 8.75 10 8.75C10.6904 8.75 11.25 9.30964 11.25 10ZM15 11.25C15.6904 11.25 16.25 10.6904 16.25 10C16.25 9.30964 15.6904 8.75 15 8.75C14.3096 8.75 13.75 9.30964 13.75 10C13.75 10.6904 14.3096 11.25 15 11.25Z" fill="#342C82" />
                          </svg>
                        </button>

                        {activeDropdown === file.id && (
                          <div className="file-upload__dropdown" ref={dropdownRef}>
                            {canPreviewFile(fileType, file.title) && (
                              <button
                                className="file-upload__dropdown-item"
                                onClick={() => handlePreview(file)}
                              >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 3C4.5 3 1.73 5.61 1 8C1.73 10.39 4.5 13 8 13C11.5 13 14.27 10.39 15 8C14.27 5.61 11.5 3 8 3ZM8 11C6.34 11 5 9.66 5 8C5 6.34 6.34 5 8 5C9.66 5 11 6.34 11 8C11 9.66 9.66 11 8 11ZM8 6.5C7.17 6.5 6.5 7.17 6.5 8C6.5 8.83 7.17 9.5 8 9.5C8.83 9.5 9.5 8.83 9.5 8C9.5 7.17 8.83 6.5 8 6.5Z" fill="#342C82" />
                                </svg>
                                Preview
                              </button>
                            )}
                            {
                              !disabled &&
                              <>
                                <button
                                  className="file-upload__dropdown-item"
                                  onClick={() => handleDownload(file)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 10L11 7H9V3H7V7H5L8 10ZM4 11V13H12V11H14V13C14 14.1046 13.1046 15 12 15H4C2.89543 15 2 14.1046 2 13V11H4Z" fill="#342C82" />
                                  </svg>
                                  Download
                                </button>
                                <button
                                  className="file-upload__dropdown-item"
                                  onClick={() => handleEdit(file)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Z" fill="#342C82" />
                                  </svg>
                                  Edit
                                </button>
                                <button
                                  className="file-upload__dropdown-item file-upload__dropdown-item--danger"
                                  onClick={() => handleDelete(file)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 2H10V3H6V2ZM3 4V5H4V14C4 14.5523 4.44772 15 5 15H11C11.5523 15 12 14.5523 12 14V5H13V4H3ZM6 6V13H7V6H6ZM9 6V13H10V6H9Z" fill="#C50F1F" />
                                  </svg>
                                  Delete
                                </button>
                              </>
                            }

                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Preview Modal */}
      {showPreview && selectedFile && (() => {
        const selectedFileType = getFileTypeFromUrl(selectedFile.url);
        return (
          <Modal
            htmlTitle={
              <div className="file-upload__preview-modal-title">
                <div className="file-upload__preview-modal-title-text">
                  <h4>{selectedFile.title}</h4>
                  <span>
                    Uploaded by {selectedFile.creator?.name || '-'} &middot; {dateOnlyFormatted(setting, selectedFile.dateCreated)}
                  </span>
                </div>
              </div>
            }
            size="large"
            handleCancel={() => setShowPreview(false)}
            contentStyle={{ justifyContent: "flex-start", height: "calc(100vh - 100px)" }}
            bodyWrapperStyle={{ display: "flex", flexDirection: "column", flex: "1 1 auto", maxHeight: "none" }}
            bodyContentStyle={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}
            body={
              <div className="file-upload__preview">
                {/* <div className="file-upload__preview-header">
                  <div className="file-upload__preview-header-main">
                    <h4 className="file-upload__preview-title">{selectedFile.title}</h4>
                    {selectedFile.notes && (
                      <div className="file-upload__preview-notes">
                        <span className="file-upload__preview-notes-label">Notes:</span>
                        <span className="file-upload__preview-notes-text">{selectedFile.notes}</span>
                      </div>
                    )}
                  </div>
                  <div className="file-upload__preview-header-meta">
                    <div className="file-upload__preview-meta-item">
                      <span className="file-upload__preview-meta-label">Uploaded by</span>
                      <span className="file-upload__preview-meta-value">{selectedFile.creator?.name || '-'}</span>
                    </div>
                    <div className="file-upload__preview-meta-item">
                      <span className="file-upload__preview-meta-label">Date</span>
                      <span className="file-upload__preview-meta-value">{dateOnlyFormatted(setting, selectedFile.dateCreated)}</span>
                    </div>
                  </div>
                </div> */}
                <div className="file-upload__preview-content">
                  {selectedFileType.startsWith('image/') ? (
                    <img className="file-upload__preview-image" src={selectedFile.url} alt={selectedFile.title} />
                  ) : selectedFileType === 'application/pdf' ? (
                    <iframe
                      src={selectedFile.url}
                      title={selectedFile.title}
                      width="100%"
                      height="100%"
                      style={{ border: 'none', borderRadius: '4px' }}
                    />
                  ) : (selectedFileType.startsWith('text/') ||selectedFileType.includes('word') || selectedFile.title.toLowerCase().endsWith('.doc') || selectedFile.title.toLowerCase().endsWith('.docx') ||
                    selectedFileType.includes('excel') || selectedFileType.includes('spreadsheet') ||
                    selectedFile.title.toLowerCase().endsWith('.xls') || selectedFile.title.toLowerCase().endsWith('.xlsx') ||
                    selectedFileType.includes('powerpoint') || selectedFileType.includes('presentation') ||
                    selectedFile.title.toLowerCase().endsWith('.ppt') || selectedFile.title.toLowerCase().endsWith('.pptx')) ? (
                    <div className="file-upload__office-preview">
                      <iframe
                        src={getPreviewUrl({
                          type: selectedFileType,
                          url: selectedFile.url,
                          name: selectedFile.title
                        })}
                        title={selectedFile.title}
                        width="100%"
                        height="100%"
                        style={{ border: 'none', borderRadius: '4px' }}
                      />
                      {/* <p className="file-upload__preview-note">
                        Preview powered by Microsoft Office Online
                      </p> */}
                    </div>
                  ): (
                    <div className="file-upload__generic-preview">
                      <div className="file-upload__file-icon">
                        <img src={getFileIcon(selectedFileType, selectedFile.title)} alt="File icon" style={{ width: '80px', height: '80px' }} />
                      </div>
                      <p>Preview not available for this file type</p>
                      <p className="file-upload__file-type"><strong>File type:</strong> {selectedFileType}</p>
                    </div>
                  )}
                </div>
              </div>
            }
            footer={false}
            enableFullScreen
          />
        );
      })()}

      {/* Delete Confirmation Modal */}
      {showDeleteOptions && fileToDelete && (
        <Modal
          title="Delete File"
          handleCancel={() => setShowDeleteOptions(false)}
          handleSave={() => handleDeleteWithOption(true)}
          saveBtnLabel="Delete"
          saveBtnClass="d_btn_danger"
          cancelBtnLabel="Cancel"
          additionalFooterButtons={[
            {
              label: "Archive",
              onClick: () => handleDeleteWithOption(false),
            }
          ]}
          body={
            <div className="file-upload__delete-confirm">
              <p>Are you sure you want to delete <strong>{fileToDelete.title}</strong>?</p>
              <p>This action cannot be undone.</p>
            </div>
          }
        />
      )}

      {/* Edit Modal */}
      {showEditModal && fileToEdit && (
        <Modal
          title="Edit File"
          handleCancel={handleCancelEdit}
          handleSave={handleSaveEdit}
          saveBtnLabel="Save Changes"
          cancelBtnLabel="Cancel"
          body={
            <div className="file-upload__edit-form">
              <div className="file-upload__form-group">
                <label htmlFor="edit-title">Title</label>
                <input
                  id="edit-title"
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="file-upload__input"
                  placeholder="Enter file title"
                  maxLength={200}
                />
              </div>
              <div className="file-upload__form-group">
                <label htmlFor="edit-notes">Notes</label>
                <textarea
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  className="file-upload__textarea"
                  placeholder="Enter notes (optional)"
                  rows={4}
                />
              </div>
            </div>
          }
        />
      )}

      {/* Upload Preparation Modal */}
      {showUploadModal && filesToUpload.length > 0 && (
        <Modal
          title={`Upload ${filesToUpload.length} File${filesToUpload.length > 1 ? 's' : ''}`}
          handleCancel={handleCancelUpload}
          handleSave={handleConfirmUpload}
          saveBtnLabel={uploading ? "Uploading..." : "Upload Files"}
          isLoading={uploading}
          cancelBtnLabel="Cancel"
          size="large"
          body={
            <div className="file-upload__edit-form">
              <p className="file-upload__upload-description">
                Configure file details before uploading. You can add titles and optional notes for each file.
              </p>
              <div className="file-upload__upload-files">
                {filesToUpload.map((fileData) => (
                  <div key={fileData.id} className="file-upload__upload-file-item">
                    <div className="file-upload__upload-file-info">
                      <div className="file-upload__upload-file-icon">
                        <img
                          src={
                            fileData.previewUrl || getFileIcon(fileData.file.type, fileData.file.name)
                          }
                          alt={fileData.file.type.startsWith('image/') ? fileData.file.name : 'File icon'}
                          onError={(e) => {
                            e.target.src = getFileIcon(fileData.file.type, fileData.file.name);
                          }}
                        />
                      </div>
                      <div className="file-upload__upload-file-name">
                        {fileData.file.name}
                      </div>
                    </div>
                    <div className="file-upload__upload-file-fields">
                      <div className="file-upload__form-group">
                        <label htmlFor={`title-${fileData.id}`}>Title</label>
                        <input
                          id={`title-${fileData.id}`}
                          type="text"
                          value={fileData.title}
                          onChange={(e) => handleFileMetadataChange(fileData.id, 'title', e.target.value)}
                          className="file-upload__input"
                          placeholder="Enter file title"
                          maxLength={200}
                        />
                      </div>
                      <div className="file-upload__form-group">
                        <label htmlFor={`notes-${fileData.id}`}>Notes</label>
                        <textarea
                          id={`notes-${fileData.id}`}
                          value={fileData.notes}
                          onChange={(e) => handleFileMetadataChange(fileData.id, 'notes', e.target.value)}
                          className="file-upload__textarea"
                          placeholder="Enter notes (optional)"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default FileUpload; 